import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";

import {
  loginUser,
  logoutUser,
  registerUser,
  getGoogleAuthUrl,
  handleGoogleCallback,
  getFacebookAuthUrl,
  handleFacebookCallback,
  refreshSessionTokens,
  getCurrentUser,
  updateCurrentUser,
  verifyEmailToken,
  uploadProfilePhoto as uploadProfilePhotoRequest,
  requestPhoneVerification as requestPhoneVerificationRequest,
  confirmPhoneVerification as confirmPhoneVerificationRequest,
  bypassPhoneVerification as bypassPhoneVerificationRequest,
  bypassEmailVerification as bypassEmailVerificationRequest
} from "../features/auth/api/auth";
import type {
  AuthLoginPayload,
  AuthRegisterPayload,
  AuthResponse,
  UpdateProfilePayload,
  UpdateProfileResult,
  ThemePreference
} from "../types/auth";
import { AUTH_EXPIRED_EVENT, ApiError } from "../services/apiClient";
import { sessionStore, storage } from "../features/shared/api/storage";
import { DEFAULT_LOCALE, getActiveLocale, normalizeLocale, setActiveLocale } from "../i18n";
import { createLogger } from "../utils/logger";
import { setClientErrorAuthToken, setClientErrorContext } from "../services/clientErrorReporter";

const logger = createLogger("AuthProvider");

export interface AuthState extends Record<string, unknown> {
  user: AuthResponse["user"] | null;
  accessToken: string | null;
  refreshToken: string | null;
  accessTokenExpiresAt: number | null;
  sessionId: string | null;
}

interface AuthContextValue extends AuthState {
  isInitializing: boolean;
  sessionExpiresAt: number | null;
  sessionRemainingMs: number | null;
  sessionTimeoutMinutes: number;
  login: (payload: AuthLoginPayload) => Promise<void>;
  register: (payload: AuthRegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithFacebook: () => Promise<void>;
  handleOAuthCallback: (code: string, state: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  updateProfile: (payload: UpdateProfilePayload) => Promise<UpdateProfileResult>;
  uploadProfilePhoto: (file: File) => Promise<void>;
  completeEmailVerification: (token: string) => Promise<AuthResponse>;
  requestPhoneVerification: (phone: string) => Promise<void>;
  verifyPhoneCode: (code: string) => Promise<AuthResponse["user"]>;
  bypassPhoneVerification: () => Promise<AuthResponse["user"]>;
  bypassEmailVerification: () => Promise<AuthResponse["user"]>;
  resetSessionTimer: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = "lotlocator-auth";
const SESSION_STORAGE_KEY = "lotlocator-auth-session";
export const AUTH_NOTICE_KEY = "lotlocator-auth-notice";
export const AUTH_REDIRECT_KEY = "lotlocator-auth-redirect";
export const AUTH_LINK_PROVIDER_KEY = "lotlocator-auth-link-provider";
export const PROFILE_NOTICE_KEY = "lotlocator-profile-notice";
const THEME_STORAGE_KEY = "lotlocator:ui-theme";

const defaultState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  accessTokenExpiresAt: null,
  sessionId: null
};

const SESSION_TIMEOUT_ENV = Number.parseInt(
  import.meta.env.VITE_SESSION_TIMEOUT_MINUTES ?? "",
  10
);
const SESSION_TIMEOUT_MINUTES =
  Number.isFinite(SESSION_TIMEOUT_ENV) && SESSION_TIMEOUT_ENV > 0
    ? SESSION_TIMEOUT_ENV
    : 60;
const SESSION_TIMEOUT_MS = SESSION_TIMEOUT_MINUTES * 60 * 1000;
const SESSION_EXPIRED_MESSAGE = "Your session expired. Please sign in again.";
const ACCESS_TOKEN_REFRESH_WINDOW_MS = 60 * 1000; // Refresh one minute before expiry

const computeSessionExpiry = (accessTokenExpiresAt?: number | null) => {
  const sessionDeadline = Date.now() + SESSION_TIMEOUT_MS;
  if (accessTokenExpiresAt && Number.isFinite(accessTokenExpiresAt)) {
    return Math.min(sessionDeadline, accessTokenExpiresAt);
  }
  return sessionDeadline;
};

const computeAccessExpiry = (expiresInSeconds?: number) => {
  if (!Number.isFinite(expiresInSeconds)) {
    return Date.now() + SESSION_TIMEOUT_MS;
  }
  return Date.now() + (expiresInSeconds as number) * 1000;
};

const resolveActiveThemePreference = (): ThemePreference => {
  if (typeof document !== "undefined") {
    const fromDataset = document.documentElement.dataset.theme;
    if (fromDataset === "light" || fromDataset === "dark") {
      return fromDataset;
    }
  }

  if (typeof window !== "undefined") {
    const stored = window.sessionStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }

    const prefersDark =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    if (prefersDark) {
      return "dark";
    }
  }

  return "light";
};

type PersistedSessionState = {
  refreshToken: string | null;
  sessionId: string | null;
  user: AuthResponse["user"] | null;
  expiresAt: number | null;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(defaultState);
  const [isInitializing, setIsInitializing] = useState(true);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);
  const [sessionRemainingMs, setSessionRemainingMs] = useState<number | null>(null);
  const authStateRef = useRef<AuthState>(defaultState);
  const sessionExpiresAtRef = useRef<number | null>(null);
  const refreshInFlight = useRef<Promise<boolean> | null>(null);

  const applyLocalePreference = useCallback((nextUser: AuthResponse["user"] | null) => {
    const locale = normalizeLocale(nextUser?.preferred_locale ?? DEFAULT_LOCALE);
    void setActiveLocale(locale);
    setClientErrorContext({ locale });
  }, []);

  const syncErrorReporter = useCallback((nextState: AuthState) => {
    setClientErrorAuthToken(nextState.accessToken);
    setClientErrorContext({
      userId: nextState.user?.id ?? null,
      sessionId: nextState.sessionId,
      userState: nextState.user
        ? nextState.user.system_role === "admin"
          ? "admin"
          : "authenticated"
        : "anonymous",
      locale: getActiveLocale()
    });
  }, []);

  useEffect(() => {
    authStateRef.current = state;
  }, [state]);

  useEffect(() => {
    syncErrorReporter(state);
  }, [state, syncErrorReporter]);

  useEffect(() => {
    sessionExpiresAtRef.current = sessionExpiresAt;
  }, [sessionExpiresAt]);

  const persist = useCallback((nextState: AuthState) => {
    const expiresAt = computeSessionExpiry(nextState.accessTokenExpiresAt);
    authStateRef.current = nextState;
    sessionExpiresAtRef.current = expiresAt;
    setState(nextState);
    setSessionExpiresAt(expiresAt);
    const persisted: PersistedSessionState = {
      refreshToken: nextState.refreshToken,
      sessionId: nextState.sessionId,
      user: nextState.user,
      expiresAt
    };
    sessionStore.set(SESSION_STORAGE_KEY, persisted);
    applyLocalePreference(nextState.user);
  }, [applyLocalePreference]);

  const persistAuthResponse = useCallback((response: AuthResponse) => {
    const accessTokenExpiresAt = computeAccessExpiry(response.expires_in);
    persist({
      user: response.user,
      accessToken: response.access_token,
      refreshToken: response.refresh_token,
      accessTokenExpiresAt,
      sessionId: response.session_id
    });
  }, [persist]);

  const clear = useCallback(() => {
    authStateRef.current = defaultState;
    sessionExpiresAtRef.current = null;
    setState(defaultState);
    setSessionExpiresAt(null);
    setSessionRemainingMs(null);
    storage.remove(STORAGE_KEY);
    sessionStore.remove(SESSION_STORAGE_KEY);
  }, []);

  const syncUser = useCallback((nextUser: AuthResponse["user"]) => {
    setState((prev) => {
      const nextState = { ...prev, user: nextUser };
      authStateRef.current = nextState;
      const expiresAt = sessionExpiresAtRef.current;
      if (expiresAt) {
        const persisted: PersistedSessionState = {
          refreshToken: nextState.refreshToken,
          sessionId: nextState.sessionId,
          user: nextState.user,
          expiresAt
        };
        sessionStore.set(SESSION_STORAGE_KEY, persisted);
      }
      return nextState;
    });
    applyLocalePreference(nextUser);
  }, [applyLocalePreference]);

  const login = useCallback<AuthContextValue["login"]>(
    async (credentials) => {
      const deviceLabel =
        credentials.device_label ??
        (typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 80) : undefined);
      const response = await loginUser({
        ...credentials,
        device_label: deviceLabel
      });
      persistAuthResponse(response);
    },
    [persistAuthResponse]
  );

  const register = useCallback<AuthContextValue["register"]>(
    async (payload) => {
      const deviceLabel =
        payload.device_label ??
        (typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 80) : undefined);
      const response = await registerUser({ ...payload, device_label: deviceLabel });
      persistAuthResponse(response);
    },
    [persistAuthResponse]
  );

  const logout = useCallback(async () => {
    if (state.accessToken) {
      try {
        await logoutUser(state.accessToken);
      } catch (error) {
        // Silently ignore logout errors but still clear local state
        logger.warn("Logout request failed", error);
      }
    }
    clear();
  }, [clear, state.accessToken]);

  const loginWithGoogle = useCallback(async () => {
    const { auth_url } = await getGoogleAuthUrl({
      locale: getActiveLocale(),
      theme: resolveActiveThemePreference()
    });
    // Store current location (or existing redirect) for redirect after OAuth
    const existingRedirect = storage.get<string>(AUTH_REDIRECT_KEY);
    const redirectPath = existingRedirect || window.location.pathname + window.location.search;
    storage.set(AUTH_REDIRECT_KEY, redirectPath);
    // Redirect to Google OAuth
    window.location.href = auth_url;
  }, []);

  const loginWithFacebook = useCallback(async () => {
    const { auth_url } = await getFacebookAuthUrl({
      locale: getActiveLocale(),
      theme: resolveActiveThemePreference()
    });
    // Store current location (or existing redirect) for redirect after OAuth
    const existingRedirect = storage.get<string>(AUTH_REDIRECT_KEY);
    const redirectPath = existingRedirect || window.location.pathname + window.location.search;
    storage.set(AUTH_REDIRECT_KEY, redirectPath);
    // Redirect to Facebook OAuth
    window.location.href = auth_url;
  }, []);

  const handleOAuthCallback = useCallback(async (code: string, state: string) => {
    // Try Google first (most common), then Facebook
    let response: AuthResponse;
    try {
      response = await handleGoogleCallback({ code, state, provider: "google" });
    } catch (googleError) {
      // If Google fails, try Facebook
      try {
        response = await handleFacebookCallback({
          code,
          state,
          provider: "facebook"
        });
      } catch (facebookError) {
        logger.error("Facebook OAuth callback failed", facebookError);
        // If both fail, throw the original error
        throw googleError;
      }
    }

    persistAuthResponse(response);
  }, [persistAuthResponse]);

  const refreshUser = useCallback(async () => {
    if (!state.accessToken) {
      return;
    }
    const me = await getCurrentUser(state.accessToken);
    syncUser(me);
  }, [state.accessToken, syncUser]);

  const uploadProfilePhoto = useCallback(
    async (file: File) => {
      if (!state.accessToken) {
        throw new Error("Not authenticated");
      }
      const updated = await uploadProfilePhotoRequest(state.accessToken, file);
      syncUser(updated);
    },
    [state.accessToken, syncUser]
  );

  const requestPhoneVerification = useCallback(
    async (phone: string) => {
      if (!state.accessToken) {
        throw new Error("Not authenticated");
      }
      await requestPhoneVerificationRequest(state.accessToken, phone);
      await refreshUser();
    },
    [refreshUser, state.accessToken]
  );

  const verifyPhoneCode = useCallback(
    async (code: string) => {
      if (!state.accessToken) {
        throw new Error("Not authenticated");
      }
      const updated = await confirmPhoneVerificationRequest(
        state.accessToken,
        code
      );
      syncUser(updated);
      return updated;
    },
    [state.accessToken, syncUser]
  );

  const bypassPhoneVerification = useCallback(async () => {
    if (!state.accessToken) {
      throw new Error("Not authenticated");
    }
    const updated = await bypassPhoneVerificationRequest(state.accessToken);
    syncUser(updated);
    return updated;
  }, [state.accessToken, syncUser]);

  const bypassEmailVerification = useCallback(async () => {
    if (!state.accessToken) {
      throw new Error("Not authenticated");
    }
    const updated = await bypassEmailVerificationRequest(state.accessToken);
    syncUser(updated);
    return updated;
  }, [state.accessToken, syncUser]);

  const updateProfile = useCallback(
    async (payload: UpdateProfilePayload) => {
      if (!state.accessToken) {
        throw new Error("Not authenticated");
      }
      const result = await updateCurrentUser(state.accessToken, payload);
      if (result.kind === "updated") {
        syncUser(result.user);
      } else {
        const me = await getCurrentUser(state.accessToken);
        syncUser(me);
      }
      return result;
    },
    [state.accessToken, syncUser]
  );

  const completeEmailVerification = useCallback(
    async (token: string) => {
      const response = await verifyEmailToken(token);
      persistAuthResponse(response);
      return response;
    },
    [persistAuthResponse]
  );

  const refreshSession = useCallback(async (tokenOverride?: string): Promise<boolean> => {
    if (refreshInFlight.current) {
      return refreshInFlight.current;
    }

    const current = authStateRef.current;
    const refreshToken = tokenOverride ?? current.refreshToken;
    if (!refreshToken) {
      return false;
    }

    const refreshPromise = (async () => {
      try {
        const response = await refreshSessionTokens({
          refresh_token: refreshToken
        });
        persistAuthResponse(response);
        return true;
      } catch (error) {
        logger.warn("Failed to refresh session", error);
        if (
          error instanceof ApiError &&
          (error.body?.error === "refresh_invalid" ||
            error.body?.error === "session_revoked")
        ) {
          sessionStore.remove(SESSION_STORAGE_KEY);
        }
        clear();
        return false;
      } finally {
        refreshInFlight.current = null;
      }
    })();

    refreshInFlight.current = refreshPromise;
    return refreshPromise;
  }, [clear, persistAuthResponse]);

  // Restore persisted auth on mount (and refresh the user to renew short-lived assets like avatar URLs)
  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      const persisted = sessionStore.get<PersistedSessionState>(SESSION_STORAGE_KEY);
      if (persisted?.refreshToken) {
        const initialExpiresAt =
          typeof persisted.expiresAt === "number" ? persisted.expiresAt : null;
        const baseState: AuthState = {
          user: persisted.user ?? null,
          accessToken: null,
          refreshToken: persisted.refreshToken,
          accessTokenExpiresAt: null,
          sessionId: persisted.sessionId ?? null
        };
        authStateRef.current = baseState;
        setState(baseState);
        if (initialExpiresAt && initialExpiresAt > Date.now()) {
          sessionExpiresAtRef.current = initialExpiresAt;
          setSessionExpiresAt(initialExpiresAt);
        }
        applyLocalePreference(baseState.user);
        const refreshed = await refreshSession(persisted.refreshToken);
        if (!refreshed && !cancelled) {
          authStateRef.current = defaultState;
          sessionExpiresAtRef.current = null;
          setState(defaultState);
          setSessionExpiresAt(null);
          sessionStore.remove(SESSION_STORAGE_KEY);
        }
      } else if (persisted) {
        sessionStore.remove(SESSION_STORAGE_KEY);
      }

      if (!cancelled) {
        setIsInitializing(false);
      }
    };

    void restoreSession();

    return () => {
      cancelled = true;
    };
  }, [applyLocalePreference, refreshSession]);

  const resetSessionTimer = useCallback(() => {
    const current = authStateRef.current;
    if (!current.refreshToken) {
      return;
    }
    const expiresAt = computeSessionExpiry(current.accessTokenExpiresAt);
    sessionExpiresAtRef.current = expiresAt;
    setSessionExpiresAt(expiresAt);
    const persisted: PersistedSessionState = {
      refreshToken: current.refreshToken,
      sessionId: current.sessionId,
      user: current.user,
      expiresAt
    };
    sessionStore.set(SESSION_STORAGE_KEY, persisted);
  }, []);

  useEffect(() => {
    if (!sessionExpiresAt || !state.accessToken) {
      setSessionRemainingMs(null);
      return;
    }

    const tick = () => {
      const diff = Math.max(sessionExpiresAt - Date.now(), 0);
      setSessionRemainingMs(diff);
    };

    tick();
    const interval = window.setInterval(tick, 1000);
    return () => {
      window.clearInterval(interval);
    };
  }, [sessionExpiresAt, state.accessToken]);

  useEffect(() => {
    if (!state.refreshToken || !state.accessTokenExpiresAt) {
      return;
    }
    const refreshAt = state.accessTokenExpiresAt - ACCESS_TOKEN_REFRESH_WINDOW_MS;
    const delay = refreshAt - Date.now();
    if (delay <= 0) {
      void refreshSession();
      return;
    }
    const timer = window.setTimeout(() => {
      void refreshSession();
    }, delay);
    return () => {
      window.clearTimeout(timer);
    };
  }, [refreshSession, state.accessTokenExpiresAt, state.refreshToken]);

  useEffect(() => {
    if (!sessionExpiresAt || !state.accessToken) {
      return;
    }
    const delay = sessionExpiresAt - Date.now();
    if (delay <= 0) {
      window.dispatchEvent(
        new CustomEvent(AUTH_EXPIRED_EVENT, {
          detail: { message: SESSION_EXPIRED_MESSAGE }
        })
      );
      return;
    }
    const timer = window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent(AUTH_EXPIRED_EVENT, {
          detail: { message: SESSION_EXPIRED_MESSAGE }
        })
      );
    }, delay);
    return () => {
      window.clearTimeout(timer);
    };
  }, [sessionExpiresAt, state.accessToken]);

  useEffect(() => {
    const handleAuthExpired = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      const message = detail?.message ?? SESSION_EXPIRED_MESSAGE;

      void (async () => {
        const refreshed = await refreshSession();
        if (refreshed) {
          return;
        }

        storage.set(AUTH_NOTICE_KEY, message);
        const redirectPath =
          window.location.pathname + window.location.search;
        storage.set(AUTH_REDIRECT_KEY, redirectPath);

        clear();

        if (window.location.pathname !== "/login") {
          window.location.assign("/login");
        }
      })();
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    };
  }, [clear, refreshSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      isInitializing,
      sessionExpiresAt,
      sessionRemainingMs,
      sessionTimeoutMinutes: SESSION_TIMEOUT_MINUTES,
      login,
      register,
      logout,
      loginWithGoogle,
      loginWithFacebook,
      handleOAuthCallback,
      refreshUser,
      uploadProfilePhoto,
      requestPhoneVerification,
      verifyPhoneCode,
      bypassPhoneVerification,
      bypassEmailVerification,
      updateProfile,
      completeEmailVerification,
      resetSessionTimer
    }),
    [
      state,
      isInitializing,
      sessionExpiresAt,
      sessionRemainingMs,
      login,
      register,
      logout,
      loginWithGoogle,
      loginWithFacebook,
      handleOAuthCallback,
      refreshUser,
      uploadProfilePhoto,
      requestPhoneVerification,
      verifyPhoneCode,
      bypassPhoneVerification,
      bypassEmailVerification,
      updateProfile,
      completeEmailVerification,
      resetSessionTimer
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }
  return ctx;
}
